import React from 'react'
import styled from 'styled-components'

// Elements
import Plaatjie from '@ubo/plaatjie'
import BreadcrumbProvider from 'components/elements/Misc/Breadcrumb'

// Interface
import { BannerProps } from 'components/flex/Banner/BannerShell'
import ButtonPrimary from 'components/elements/Buttons/ButtonPrimary'

const Section = styled.section`
  height: calc(100vh - 200px);

  @media (max-width: 991.98px) {
    height: calc(90vh - 100px);
  }
`

const BannerWrapper = styled.div`
  position: relative;
  overflow: hidden;
  height: 100%;
`

const HeroImage = styled(Plaatjie)`
  position: absolute !important;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  & img,
  & picture img {
    object-fit: cover;
    width: 100%;
    height: 100%;

    @media (max-width: 991.98px) {
      object-position: 30% 50% !important;
    }
  }
`

const ContentColumn = styled.div`
  position: relative;
  z-index: 2;
  min-height: 90%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 991.98px) {
    min-height: 95%;
  }
`

const InfoBar = styled.div`
  background-color: ${({ theme }) => theme.color.primary};
  padding: 2rem;

  @media (max-width: 991.98px) {
    background-color: rgba(24, 38, 63, 0.75);
  }

  @media (min-width: 992px) {
    padding: 1.5rem 2rem;
  }
`

const InfoBarInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (min-width: 992px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
  }
`

const Eyebrow = styled.span`
  display: block;
  color: ${({ theme }) => theme.color.light};
  font-size: ${({ theme }) => theme.font.size.big};
  font-weight: ${({ theme }) => theme.font.weight.light};
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`

const TitleContent = styled.h1`
  color: ${({ theme }) => theme.color.light};
  font-weight: ${({ theme }) => theme.font.weight.extraBold};
  margin-bottom: 0;

  @media (min-width: 992px) {
    font-size: ${({ theme }) => theme.font.size.mega};
    line-height: 1.15;
  }

  @media (max-width: 991.98px) {
    font-size: ${({ theme }) => theme.font.size.huge};
    line-height: 1.2;
  }
`

const MetaRow = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
`

const MetaItem = styled.li`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.color.light};
  font-size: ${({ theme }) => theme.font.size.medium};
  gap: 0.5rem;

  @media (max-width: 575.98px) {
    font-size: ${({ theme }) => theme.font.size.small};
  }
`

const CtaWrap = styled.div`
  flex-shrink: 0;

  @media (max-width: 991.98px) {
    align-self: flex-start;
  }
`

const BannerVacancy: React.FC<BannerProps> = ({ fields, pageContext }) => {
  const benefits = (fields.benefits || [])
    .map((b) => b?.benefit)
    .filter(Boolean) as string[]

  const cta = fields.links?.[0]?.link

  return (
    <Section className="mb-5 pb-lg-3">
      <BannerWrapper>
        <HeroImage image={fields.image} alt="" />
        <ContentColumn>
          <div className="container px-3 pt-4">
            <div className="d-flex align-items-start">
              <BreadcrumbProvider
                pageContext={pageContext}
                className="text-white"
              />
            </div>
          </div>
          <div className="container px-3">
            <InfoBar>
              <InfoBarInner>
                <div>
                  <Eyebrow>Vacature</Eyebrow>
                  {fields.description ? (
                    <TitleContent>{fields.title}</TitleContent>
                  ) : null}
                  {benefits.length > 0 && (
                    <MetaRow>
                      {benefits.map((text) => (
                        <MetaItem key={text}>
                          <div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="9"
                              height="14"
                              viewBox="0 0 9 14"
                              fill="none"
                            >
                              <path
                                d="M9 6.98579L3.67393 4.88054e-07L2.70244e-07 1.66869e-07L5.01241 6.98579L0 14L3.67393 14L9 6.98579Z"
                                fill="#FFCD0A"
                              />
                            </svg>
                          </div>
                          {text}
                        </MetaItem>
                      ))}
                    </MetaRow>
                  )}
                </div>
                {cta?.url && (
                  <CtaWrap className="pe-lg-5">
                    <ButtonPrimary
                      to={cta.url || '/'}
                      target={cta.target || ''}
                      className="text-uppercase"
                    >
                      {cta.title || 'Solliciteer nu'}
                    </ButtonPrimary>
                  </CtaWrap>
                )}
              </InfoBarInner>
            </InfoBar>
          </div>
        </ContentColumn>
      </BannerWrapper>
    </Section>
  )
}

export default BannerVacancy
